import { collection, doc, onSnapshot, orderBy, query, updateDoc, Timestamp, getDoc } from 'firebase/firestore'
import { getAuth } from 'firebase/auth'
import { useEffect, useState, type ReactNode } from 'react'
import { db } from '../../firebase'
import { useNavigate } from 'react-router-dom'
import { 
  Box, 
  Button, 
  Stack, 
  Typography, 
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  ImageList,
  ImageListItem,
  Modal,
  Backdrop,
  Fade
} from '@mui/material'
import { 
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  ZoomIn as ZoomInIcon,
  Delete as DeleteIcon
} from '@mui/icons-material'
import { localizeCategoryList, loadCategoryCatalog, listHasCategory, type CategoryCatalogItem } from '../../utils/categoryResolver'
import {
  CAR_SPARE_PARTS_BRANDS,
  CAR_SPARE_PARTS_CONDITIONS,
  CAR_SPARE_PARTS_SECTIONS,
  isCarSparePartsCategoryValue,
  parseCarSparePartsSpecialization,
} from '../../constants/carSparePartsConstants'
import {
  parseCategoryFilters,
  type CategoryFilterDef,
  type CategoryFiltersConfig,
} from '../settings/CategoryFiltersEditor'

interface StoreActivationRequestDoc {
  id: string
  sellerId?: string
  sellerName?: string
  sellerPhone?: string
  storeName?: string
  storePhone?: string
  storeAddress?: string
  latitude?: number
  longitude?: number
  locationName?: string
  businessType?: string
  requestedCategories?: string[]
  hasDelivery?: boolean
  hasElectronicPayment?: boolean
  hasInstallment?: boolean
  hasStorePickup?: boolean
  workingHours?: Record<string, { from: string; to: string; enabled: string }>
  storeImageUrl?: string
  ownerName?: string
  ownerPhone?: string
  ownerEmail?: string
  nationalIdFrontUrl?: string
  nationalIdBackUrl?: string
  commercialRegisterUrl?: string
  taxCardUrl?: string
  businessLicenseUrl?: string
  status?: string
  rejectionReason?: string
  reviewedBy?: string
  reviewedAt?: any
  submittedAt?: any
  updatedAt?: any
  approvedAt?: any
  termsAccepted?: boolean
  carSparePartsSpecialization?: {
    conditions?: string[]
    brands?: string[]
    sections?: string[]
  }
  /** تخصص الفلاتر الحديثة: مفتاح = معرّف الفئة، والقيم ids للخيارات */
  categoryFiltersSpecialization?: Record<
    string,
    {
      conditions?: string[]
      brands?: string[]
      sections?: string[]
    }
  >
}

type RequestStatus = 'pending' | 'approved' | 'rejected' | 'all'

const statusMap: Record<string, { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' }> = {
  'pending': { label: 'في انتظار المراجعة', color: 'warning' },
  'approved': { label: 'موافق عليه', color: 'success' },
  'rejected': { label: 'مرفوض', color: 'error' }
}

export default function StoreActivationRequestsPage() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<StoreActivationRequestDoc[]>([])
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)
  const [statusFilter, setStatusFilter] = useState<RequestStatus>('all')
  const [detailsDialog, setDetailsDialog] = useState<{ open: boolean; request?: StoreActivationRequestDoc }>({ open: false })
  const [approveDialog, setApproveDialog] = useState<{ open: boolean; requestId?: string }>({ open: false })
  const [rejectDialog, setRejectDialog] = useState<{ open: boolean; requestId?: string; reason: string }>({ open: false, reason: '' })
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; requestId?: string }>({ open: false })
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' })
  const [searchQuery, setSearchQuery] = useState('')
  const [imageModal, setImageModal] = useState<{ open: boolean; url?: string }>({ open: false })
  const [currentAdminId, setCurrentAdminId] = useState<string>('')
  /** منع النقر المزدوج: تعطيل أزرار الموافقة/الرفض حتى انتهاء الطلب الحالي */
  const [actionInProgressRequestId, setActionInProgressRequestId] = useState<string | null>(null)
  const [resolvedCategoryLabels, setResolvedCategoryLabels] = useState<string[]>([])
  const [categoryCatalog, setCategoryCatalog] = useState<CategoryCatalogItem[]>([])
  /** فلاتر الفئات لطلب التفاصيل — تُجلب مباشرة من Firestore لضمان ظهور كل التخصصات */
  const [detailFiltersByCatId, setDetailFiltersByCatId] = useState<
    Record<string, CategoryFiltersConfig>
  >({})

  useEffect(() => {
    loadCategoryCatalog().then(setCategoryCatalog).catch(() => setCategoryCatalog([]))
  }, [])

  // عند فتح التفاصيل: حدّث الكتالوج + حمّل فلاتر كل الفئات المطلوبة
  useEffect(() => {
    if (!detailsDialog.open || !detailsDialog.request) {
      setDetailFiltersByCatId({})
      return
    }
    let cancelled = false
    const request = detailsDialog.request

    ;(async () => {
      try {
        const catalog = await loadCategoryCatalog(true)
        if (!cancelled) setCategoryCatalog(catalog)

        const idSet = new Set<string>()
        for (const raw of request.requestedCategories || []) {
          const hit = catalog.find((c) => listHasCategory([raw], c) || c.id === raw)
          if (hit) idSet.add(hit.id)
          else if (String(raw || '').trim()) idSet.add(String(raw).trim())
        }
        for (const key of Object.keys(request.categoryFiltersSpecialization || {})) {
          const hit = catalog.find((c) => listHasCategory([key], c) || c.id === key)
          if (hit) idSet.add(hit.id)
          else idSet.add(key)
        }

        const next: Record<string, CategoryFiltersConfig> = {}
        await Promise.all(
          Array.from(idSet).map(async (catId) => {
            const fromCatalog = catalog.find((c) => c.id === catId)
            let rawFilters = fromCatalog?.categoryFilters
            if (!rawFilters) {
              try {
                const snap = await getDoc(doc(db, 'categories', catId))
                if (snap.exists()) rawFilters = (snap.data() as any)?.categoryFilters
              } catch {
                /* ignore */
              }
            }
            next[catId] = parseCategoryFilters(rawFilters)
          }),
        )
        if (!cancelled) setDetailFiltersByCatId(next)
      } catch {
        if (!cancelled) setDetailFiltersByCatId({})
      }
    })()

    return () => {
      cancelled = true
    }
  }, [detailsDialog.open, detailsDialog.request?.id])

  const requestHasCarSpareParts = (request?: StoreActivationRequestDoc): boolean => {
    if (!request?.requestedCategories?.length) return false
    const carCat = categoryCatalog.find(
      (c) =>
        isCarSparePartsCategoryValue(c.id) ||
        isCarSparePartsCategoryValue(c.name) ||
        isCarSparePartsCategoryValue(c.nameEn),
    )
    if (carCat && listHasCategory(request.requestedCategories, carCat)) return true
    return request.requestedCategories.some((v) => isCarSparePartsCategoryValue(v))
  }

  /** كل خيارات العرض = قائمة المرجع + أي قيم اختارها البائع وغير موجودة في المرجع */
  const mergeChipOptions = (
    referenceOptions: readonly string[],
    selected: string[] | undefined,
  ): string[] => {
    const out: string[] = []
    const seen = new Set<string>()
    for (const raw of [...referenceOptions, ...(selected ?? [])]) {
      const item = String(raw ?? '').trim()
      if (!item || seen.has(item)) continue
      seen.add(item)
      out.push(item)
    }
    return out
  }

  /** أقسام «قطع الغيار» من Firestore — النشطة فقط (المحذوفة/المعطّلة لا تظهر) */
  const resolveCarSparePartsSectionOptions = (
    request?: StoreActivationRequestDoc,
  ): string[] => {
    const fromCat = (c: CategoryCatalogItem | undefined): string[] =>
      (c?.subSections ?? [])
        .filter((s) => s.isActive !== false)
        .map((s) => s.name.trim())
        .filter(Boolean)

    if (request?.requestedCategories?.length) {
      for (const c of categoryCatalog) {
        if (!listHasCategory(request.requestedCategories, c)) continue
        if (
          isCarSparePartsCategoryValue(c.id) ||
          isCarSparePartsCategoryValue(c.name) ||
          isCarSparePartsCategoryValue(c.nameEn)
        ) {
          const names = fromCat(c)
          if (names.length > 0) return names
        }
      }
    }

    const carCat = categoryCatalog.find(
      (c) =>
        isCarSparePartsCategoryValue(c.id) ||
        isCarSparePartsCategoryValue(c.name) ||
        isCarSparePartsCategoryValue(c.nameEn),
    )
    const names = fromCat(carCat)
    if (names.length > 0) return names
    return [...CAR_SPARE_PARTS_SECTIONS]
  }

  const normalizeFilterToken = (input: string): string => {
    let s = String(input ?? '').toLowerCase().trim()
    s = s
      .replace(/[ًٌٍَُِّْ]/g, '')
      .replace(/[أإآٱ]/g, 'ا')
      .replace(/ى/g, 'ي')
      .replace(/ة/g, 'ه')
      .replace(/ؤ/g, 'و')
      .replace(/ئ/g, 'ي')
    s = s.replace(/[-_،,./\\|]+/g, ' ')
    s = s.replace(/\s+/g, ' ').trim()
    return s
  }

  /** يحوّل id أو اسم قديم إلى تسمية عرض من إعدادات الفلاتر. */
  const resolveOptionLabel = (def: CategoryFilterDef, raw: string): string => {
    const value = String(raw ?? '').trim()
    if (!value) return ''
    const byId = def.options.find((o) => o.id === value)
    if (byId) return byId.name || byId.nameEn || value
    const n = normalizeFilterToken(value)
    const byName = def.options.find(
      (o) =>
        normalizeFilterToken(o.name) === n ||
        normalizeFilterToken(o.nameEn || '') === n,
    )
    if (byName) return byName.name || byName.nameEn || value
    // لا نعرض مفاتيح تقنية مثل cond_0 إن وُجدت بدون مطابقة
    if (/^(cond|brand|section|opt|filter)_\d+$/i.test(value)) return value
    return value
  }

  const isOptionSelected = (def: CategoryFilterDef, optionId: string, selectedRaw: string[]): boolean => {
    const selectedIds = new Set(
      selectedRaw.map((s) => {
        const value = String(s ?? '').trim()
        if (!value) return ''
        if (def.options.some((o) => o.id === value)) return value
        const n = normalizeFilterToken(value)
        const hit = def.options.find(
          (o) =>
            normalizeFilterToken(o.name) === n ||
            normalizeFilterToken(o.nameEn || '') === n,
        )
        return hit?.id || value
      }).filter(Boolean),
    )
    return selectedIds.has(optionId)
  }

  const renderFilterDefChipGroup = (
    title: string,
    def: CategoryFilterDef,
    selectedRaw: string[] | undefined,
  ) => {
    const options = [...(def.options || [])]
      .filter((o) => o.isActive !== false)
      .sort((a, b) => (a.order !== b.order ? a.order - b.order : a.name.localeCompare(b.name, 'ar')))
    const selected = (selectedRaw ?? []).map((s) => String(s).trim()).filter(Boolean)
    const selectedCount = options.filter((o) => isOptionSelected(def, o.id, selected)).length

    // اختيارات غير موجودة في المرجع الحالي — نعرضها باسمها المحلول
    const orphanSelected = selected.filter(
      (s) =>
        !options.some(
          (o) =>
            o.id === s ||
            normalizeFilterToken(o.name) === normalizeFilterToken(s) ||
            normalizeFilterToken(o.nameEn || '') === normalizeFilterToken(s),
        ),
    )

    return (
      <Box mt={1.5}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {title} ({selectedCount}/{options.length || selected.length})
        </Typography>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {options.map((opt) => {
            const isOn = isOptionSelected(def, opt.id, selected)
            return (
              <Chip
                key={`${title}-${opt.id}`}
                label={opt.name || opt.nameEn || opt.id}
                size="small"
                color={isOn ? 'primary' : 'default'}
                variant={isOn ? 'filled' : 'outlined'}
                sx={{ opacity: isOn ? 1 : 0.45 }}
              />
            )
          })}
          {orphanSelected.map((raw) => (
            <Chip
              key={`${title}-orphan-${raw}`}
              label={resolveOptionLabel(def, raw)}
              size="small"
              color="primary"
              variant="filled"
            />
          ))}
        </Stack>
      </Box>
    )
  }

  const renderCategoryFiltersSpecialization = (request: StoreActivationRequestDoc) => {
    const byCat = request.categoryFiltersSpecialization || {}
    const blocks: ReactNode[] = []

    const resolveCat = (raw: string): CategoryCatalogItem | null => {
      const v = String(raw || '').trim()
      if (!v) return null
      return (
        categoryCatalog.find((c) => c.id === v || listHasCategory([v], c)) || null
      )
    }

    // كل الفئات: المطلوبة + الموجودة في تخصصات الفلاتر المحفوظة
    const catIdsOrdered: string[] = []
    const seen = new Set<string>()
    const pushId = (id: string) => {
      const t = id.trim()
      if (!t || seen.has(t)) return
      seen.add(t)
      catIdsOrdered.push(t)
    }
    for (const raw of request.requestedCategories || []) {
      const hit = resolveCat(raw)
      pushId(hit?.id || String(raw))
    }
    for (const key of Object.keys(byCat)) {
      const hit = resolveCat(key)
      pushId(hit?.id || key)
    }

    const findSpecForCat = (catId: string, cat: CategoryCatalogItem | null) => {
      if (byCat[catId]) return byCat[catId]
      if (cat?.name && byCat[cat.name]) return byCat[cat.name]
      const entry = Object.entries(byCat).find(([key]) => {
        if (key === catId) return true
        if (cat && listHasCategory([key], cat)) return true
        return false
      })
      return entry?.[1]
    }

    for (const catId of catIdsOrdered) {
      const cat = resolveCat(catId)
      const filters =
        detailFiltersByCatId[catId] ||
        detailFiltersByCatId[cat?.id || ''] ||
        parseCategoryFilters(cat?.categoryFilters)
      const enabled: Array<{
        def: CategoryFilterDef
        listKey: 'conditions' | 'brands' | 'sections'
      }> = []
      if (filters.filter1.enabled) {
        enabled.push({ def: filters.filter1, listKey: 'conditions' })
      }
      if (filters.filter2.enabled) {
        enabled.push({ def: filters.filter2, listKey: 'brands' })
      }
      if (filters.filter3.enabled) {
        enabled.push({ def: filters.filter3, listKey: 'sections' })
      }

      const spec = findSpecForCat(catId, cat)
      const isCar =
        isCarSparePartsCategoryValue(catId) ||
        isCarSparePartsCategoryValue(cat?.id) ||
        isCarSparePartsCategoryValue(cat?.name) ||
        isCarSparePartsCategoryValue(cat?.nameEn)

      // لا نعرض الفئة إن لم يكن لها فلاتر مفعّلة وليس لها تخصص محفوظ
      if (enabled.length === 0 && !spec) {
        // توافق طلبات قطع الغيار القديمة
        if (!(isCar && request.carSparePartsSpecialization)) continue
      }

      const title = cat?.name || cat?.nameEn || catId
      const legacySpec =
        !spec && isCar
          ? parseCarSparePartsSpecialization(request.carSparePartsSpecialization)
          : null
      const effectiveSpec = spec || legacySpec || undefined

      // إن وُجدت فلاتر مفعّلة نعرضها؛ وإلا نعرض مسار قطع الغيار القديم
      if (enabled.length > 0) {
        blocks.push(
          <Box
            key={`filters-${catId}`}
            mt={2}
            p={1.5}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'rgba(255, 102, 0, 0.04)',
            }}
          >
            <Typography variant="subtitle2" color="primary" gutterBottom>
              تخصصات: {title}
            </Typography>
            {!effectiveSpec ? (
              <Typography variant="body2" color="error">
                لم يُحدَّد أي تخصص لهذه الفئة
              </Typography>
            ) : (
              enabled.map(({ def, listKey }) =>
                renderFilterDefChipGroup(
                  def.nameAr || def.nameEn || listKey,
                  def,
                  (effectiveSpec as Record<string, string[] | undefined>)[listKey],
                ),
              )
            )}
          </Box>,
        )
        continue
      }

      if (isCar && (effectiveSpec || request.carSparePartsSpecialization)) {
        const carSpec =
          effectiveSpec ||
          parseCarSparePartsSpecialization(request.carSparePartsSpecialization)
        blocks.push(
          <Box
            key={`legacy-car-${catId}`}
            mt={2}
            p={1.5}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'rgba(255, 102, 0, 0.04)',
            }}
          >
            <Typography variant="subtitle2" color="primary" gutterBottom>
              تخصصات: {title}
            </Typography>
            {!carSpec ? (
              <Typography variant="body2" color="error">
                لم يُحدَّد أي تخصص (حالة / ماركة / قسم)
              </Typography>
            ) : (
              <>
                {renderCarPartsChipGroup('الحالة', CAR_SPARE_PARTS_CONDITIONS, carSpec.conditions)}
                {renderCarPartsChipGroup('الماركة', CAR_SPARE_PARTS_BRANDS, carSpec.brands)}
                {renderCarPartsChipGroup(
                  'القسم',
                  resolveCarSparePartsSectionOptions(request),
                  carSpec.sections,
                  { strictToReference: true },
                )}
              </>
            )}
          </Box>,
        )
      }
    }

    return <>{blocks}</>
  }

  const renderCarPartsChipGroup = (
    title: string,
    allOptions: readonly string[],
    selected: string[] | undefined,
    opts?: { /** للأقسام: لا تُعرض قيم محذوفة من لوحة التحكم حتى لو بقيت في الطلب القديم */
      strictToReference?: boolean },
  ) => {
    const selectedRaw = Array.from(
      new Set((selected ?? []).map((s) => String(s).trim()).filter(Boolean)),
    )
    const referenceList = allOptions.map((s) => String(s).trim()).filter(Boolean)
    const referenceSet = new Set(referenceList)

    // تحويل ids القديمة (cond_0 / brand_12) إلى أسماء العرض
    const selectedLabels = new Set<string>()
    for (const s of selectedRaw) {
      const indexed = /^(?:cond|brand|section)_(\d+)$/i.exec(s)
      if (indexed) {
        const name = referenceList[Number(indexed[1])]
        if (name) selectedLabels.add(name)
        continue
      }
      if (referenceSet.has(s)) {
        selectedLabels.add(s)
        continue
      }
      // اسم قديم مطابق بعد التطبيع
      const n = normalizeFilterToken(s)
      const hit = referenceList.find((r) => normalizeFilterToken(r) === n)
      if (hit) selectedLabels.add(hit)
      else if (!opts?.strictToReference) selectedLabels.add(s)
    }

    const displayItems = opts?.strictToReference
      ? referenceList
      : mergeChipOptions(referenceList, Array.from(selectedLabels).filter((x) => !referenceSet.has(x)))

    return (
      <Box mt={1.5}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {title} ({selectedLabels.size}/{displayItems.length})
        </Typography>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {displayItems.map((item) => {
            const isOn = selectedLabels.has(item)
            return (
              <Chip
                key={`${title}-${item}`}
                label={item}
                size="small"
                color={isOn ? 'primary' : 'default'}
                variant={isOn ? 'filled' : 'outlined'}
                sx={{ opacity: isOn ? 1 : 0.45 }}
              />
            )
          })}
        </Stack>
      </Box>
    )
  }

  useEffect(() => {
    const cats = detailsDialog.request?.requestedCategories
    if (!detailsDialog.open || !cats?.length) {
      setResolvedCategoryLabels([])
      return
    }
    let cancelled = false
    localizeCategoryList(cats, 'ar').then((labels) => {
      if (!cancelled) setResolvedCategoryLabels(labels)
    })
    return () => {
      cancelled = true
    }
  }, [detailsDialog.open, detailsDialog.request?.id, detailsDialog.request?.requestedCategories?.join('|')])

  useEffect(() => {
    // الحصول على معرف الأدمن الحالي
    const auth = getAuth()
    if (auth.currentUser) {
      setCurrentAdminId(auth.currentUser.uid)
    }

    const q = query(collection(db, 'store_activation_requests'), orderBy('submittedAt', 'desc'))
    const unsub = onSnapshot(
      q, 
      (snapshot) => {
        const requests: StoreActivationRequestDoc[] = snapshot.docs.map(d => ({ 
          id: d.id, 
          ...(d.data() as any)
        }))
        setRows(requests)
      },
      (error) => {
        console.error('خطأ في جلب طلبات تفعيل المتجر:', error)
        setSnackbar({ open: true, message: `خطأ في جلب الطلبات: ${error.message}`, severity: 'error' })
      }
    )

    return () => unsub()
  }, [])

  const filteredRows = rows.filter(row => {
    const matchesStatus = statusFilter === 'all' || row.status === statusFilter
    const matchesSearch = !searchQuery || 
      row.storeName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.sellerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.sellerPhone?.includes(searchQuery) ||
      row.storeAddress?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.ownerName?.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesStatus && matchesSearch
  })

  const paginatedRows = filteredRows.slice((page - 1) * pageSize, page * pageSize)
  const totalPages = Math.ceil(filteredRows.length / pageSize)

  const handleApprove = async () => {
    if (!approveDialog.requestId || !currentAdminId) return

    setActionInProgressRequestId(approveDialog.requestId)
    try {
      // التحقق من أن المستخدم أدمن
      const auth = getAuth()
      if (!auth.currentUser || !auth.currentUser.email) {
        setSnackbar({ open: true, message: 'يجب تسجيل الدخول كأدمن', severity: 'error' })
        return
      }

      // التحقق من وجود البريد في admin_emails
      const adminEmailRef = doc(db, 'admin_emails', auth.currentUser.email)
      const adminEmailDoc = await getDoc(adminEmailRef)
      if (!adminEmailDoc.exists()) {
        setSnackbar({ 
          open: true, 
          message: 'ليس لديك صلاحيات الأدمن. سيتم توجيهك لصفحة Bootstrap.', 
          severity: 'error' 
        })
        setTimeout(() => navigate('/bootstrap'), 2000)
        return
      }

      // استدعاء دالة الموافقة من الخدمة
      const { approveStoreActivation } = await import('../../services/storeActivationService')
      await approveStoreActivation(approveDialog.requestId, currentAdminId)
      
      setSnackbar({ open: true, message: 'تم الموافقة على طلب تفعيل المتجر بنجاح', severity: 'success' })
      setApproveDialog({ open: false })
    } catch (error: any) {
      console.error('خطأ في الموافقة:', error)
      if (error.code === 'permission-denied' || error.message?.includes('permission')) {
        setSnackbar({ 
          open: true, 
          message: 'ليس لديك صلاحيات الأدمن. يرجى استخدام صفحة Bootstrap لإضافة حسابك.', 
          severity: 'error' 
        })
      } else {
        setSnackbar({ open: true, message: `${error.message || 'خطأ في الموافقة'}`, severity: 'error' })
      }
    } finally {
      setActionInProgressRequestId(null)
    }
  }

  const handleReject = async () => {
    if (!rejectDialog.requestId || !rejectDialog.reason.trim() || !currentAdminId) {
      setSnackbar({ open: true, message: 'يرجى إدخال سبب الرفض', severity: 'error' })
      return
    }

    setActionInProgressRequestId(rejectDialog.requestId)
    try {
      // التحقق من أن المستخدم أدمن
      const auth = getAuth()
      if (!auth.currentUser || !auth.currentUser.email) {
        setSnackbar({ open: true, message: 'يجب تسجيل الدخول كأدمن', severity: 'error' })
        return
      }

      // التحقق من وجود البريد في admin_emails
      const adminEmailRef = doc(db, 'admin_emails', auth.currentUser.email)
      const adminEmailDoc = await getDoc(adminEmailRef)
      if (!adminEmailDoc.exists()) {
        setSnackbar({ 
          open: true, 
          message: 'ليس لديك صلاحيات الأدمن. سيتم توجيهك لصفحة Bootstrap.', 
          severity: 'error' 
        })
        setTimeout(() => navigate('/bootstrap'), 2000)
        return
      }

      // استدعاء دالة الرفض من الخدمة
      const { rejectStoreActivation } = await import('../../services/storeActivationService')
      await rejectStoreActivation(rejectDialog.requestId, currentAdminId, rejectDialog.reason.trim())
      
      setSnackbar({ open: true, message: 'تم رفض طلب تفعيل المتجر', severity: 'success' })
      setRejectDialog({ open: false, reason: '' })
    } catch (error: any) {
      console.error('خطأ في الرفض:', error)
      if (error.code === 'permission-denied' || error.message?.includes('permission')) {
        setSnackbar({ 
          open: true, 
          message: 'ليس لديك صلاحيات الأدمن. يرجى استخدام صفحة Bootstrap لإضافة حسابك.', 
          severity: 'error' 
        })
      } else {
        setSnackbar({ open: true, message: `${error.message || 'خطأ في الرفض'}`, severity: 'error' })
      }
    } finally {
      setActionInProgressRequestId(null)
    }
  }

  const openImageModal = (url: string) => {
    setImageModal({ open: true, url })
  }

  const handleDelete = async () => {
    if (!deleteDialog.requestId || !currentAdminId) {
      setSnackbar({ open: true, message: 'خطأ في بيانات الطلب', severity: 'error' })
      return
    }

    try {
      // التحقق من أن المستخدم أدمن
      const auth = getAuth()
      if (!auth.currentUser || !auth.currentUser.email) {
        setSnackbar({ open: true, message: 'يجب تسجيل الدخول كأدمن', severity: 'error' })
        return
      }

      // التحقق من وجود البريد في admin_emails
      const adminEmailRef = doc(db, 'admin_emails', auth.currentUser.email)
      const adminEmailDoc = await getDoc(adminEmailRef)
      if (!adminEmailDoc.exists()) {
        setSnackbar({ 
          open: true, 
          message: 'ليس لديك صلاحيات الأدمن. سيتم توجيهك لصفحة Bootstrap.', 
          severity: 'error' 
        })
        setTimeout(() => navigate('/bootstrap'), 2000)
        return
      }

      // استدعاء دالة الحذف من الخدمة
      const { deleteStoreActivationRequest } = await import('../../services/storeActivationService')
      await deleteStoreActivationRequest(deleteDialog.requestId, currentAdminId)
      
      setSnackbar({ open: true, message: 'تم حذف طلب تفعيل المتجر بنجاح', severity: 'success' })
      setDeleteDialog({ open: false })
    } catch (error: any) {
      console.error('خطأ في الحذف:', error)
      if (error.code === 'permission-denied' || error.message?.includes('permission')) {
        setSnackbar({ 
          open: true, 
          message: 'ليس لديك صلاحيات الأدمن. يرجى استخدام صفحة Bootstrap لإضافة حسابك.', 
          severity: 'error' 
        })
      } else {
        setSnackbar({ open: true, message: `خطأ في الحذف: ${error.message}`, severity: 'error' })
      }
    }
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">طلبات تفعيل المتجر</Typography>
      </Stack>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            placeholder="بحث..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>فلترة حسب الحالة</InputLabel>
            <Select
              value={statusFilter}
              label="فلترة حسب الحالة"
              onChange={(e) => setStatusFilter(e.target.value as RequestStatus)}
            >
              <MenuItem value="all">الكل</MenuItem>
              <MenuItem value="pending">في انتظار المراجعة</MenuItem>
              <MenuItem value="approved">موافق عليه</MenuItem>
              <MenuItem value="rejected">مرفوض</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>اسم المتجر</TableCell>
              <TableCell>اسم التاجر</TableCell>
              <TableCell>هاتف التاجر</TableCell>
              <TableCell>نوع النشاط</TableCell>
              <TableCell>الحالة</TableCell>
              <TableCell>تاريخ الإرسال</TableCell>
              <TableCell>الإجراءات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.storeName || '-'}</TableCell>
                <TableCell>{row.sellerName || '-'}</TableCell>
                <TableCell>{row.sellerPhone || '-'}</TableCell>
                <TableCell>{row.businessType || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={statusMap[row.status || 'pending']?.label || row.status}
                    color={statusMap[row.status || 'pending']?.color || 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {row.submittedAt?.toDate ? new Date(row.submittedAt.toDate()).toLocaleDateString('ar-EG') : '-'}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    <Tooltip title="عرض التفاصيل">
                      <IconButton
                        size="small"
                        onClick={() => setDetailsDialog({ open: true, request: row })}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    {row.status === 'pending' && (
                      <>
                        <Tooltip title={actionInProgressRequestId ? 'جاري المعالجة...' : 'الموافقة'}>
                          <span>
                            <IconButton
                              size="small"
                              color="success"
                              disabled={!!actionInProgressRequestId}
                              onClick={() => setApproveDialog({ open: true, requestId: row.id })}
                            >
                              <CheckCircleIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={actionInProgressRequestId ? 'جاري المعالجة...' : 'الرفض'}>
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={!!actionInProgressRequestId}
                              onClick={() => setRejectDialog({ open: true, requestId: row.id, reason: '' })}
                            >
                              <CloseIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    )}
                    {row.status === 'approved' && (
                      <Tooltip title={actionInProgressRequestId ? 'جاري المعالجة...' : 'رفض الطلب (إلغاء الموافقة)'}>
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={!!actionInProgressRequestId}
                            onClick={() => setRejectDialog({ open: true, requestId: row.id, reason: '' })}
                          >
                            <CloseIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title="حذف الطلب">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteDialog({ open: true, requestId: row.id })}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      {/* تفاصيل الطلب */}
      <Dialog
        open={detailsDialog.open}
        onClose={() => setDetailsDialog({ open: false })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">تفاصيل طلب تفعيل المتجر</Typography>
            {detailsDialog.request && (
              <Chip
                label={statusMap[detailsDialog.request.status || 'pending']?.label || detailsDialog.request.status}
                color={statusMap[detailsDialog.request.status || 'pending']?.color || 'default'}
                size="small"
              />
            )}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {detailsDialog.request && (
            <Stack spacing={3} mt={1}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">بيانات المتجر</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">اسم المتجر</Typography>
                      <Typography variant="body1" fontWeight="medium">{detailsDialog.request.storeName || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">هاتف المتجر</Typography>
                      <Typography variant="body1" fontWeight="medium">{detailsDialog.request.storePhone || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" color="text.secondary">عنوان المتجر</Typography>
                        <Chip label="اختياري" size="small" variant="outlined" />
                      </Stack>
                      <Typography variant="body1" fontWeight="medium">{detailsDialog.request.storeAddress || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" color="text.secondary">الموقع الجغرافي</Typography>
                        <Chip label="إجباري" size="small" color="warning" variant="outlined" />
                      </Stack>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} mt={0.5}>
                        <Typography variant="body1" fontWeight="medium">{detailsDialog.request.locationName || '-'}</Typography>
                        {detailsDialog.request.latitude && detailsDialog.request.longitude && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              const lat = detailsDialog.request!.latitude!
                              const lng = detailsDialog.request!.longitude!
                              window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank')
                            }}
                          >
                            فتح في الخرائط
                          </Button>
                        )}
                      </Stack>
                      {detailsDialog.request.latitude && detailsDialog.request.longitude && (
                        <Typography variant="caption" color="text.secondary">
                          الإحداثيات: {detailsDialog.request.latitude.toFixed(6)}, {detailsDialog.request.longitude.toFixed(6)}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">نوع النشاط</Typography>
                      <Typography variant="body1" fontWeight="medium">{detailsDialog.request.businessType || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">الفئات المطلوبة</Typography>
                      <Box mt={0.5}>
                        {resolvedCategoryLabels.length > 0 ? (
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {resolvedCategoryLabels.map((label, idx) => (
                              <Chip key={idx} label={label} size="small" variant="outlined" />
                            ))}
                          </Stack>
                        ) : detailsDialog.request.requestedCategories && detailsDialog.request.requestedCategories.length > 0 ? (
                          <Typography variant="body2" color="text.secondary">جاري تحميل الفئات...</Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </Box>
                      {renderCategoryFiltersSpecialization(detailsDialog.request)}
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">الخدمات المتاحة</Typography>
                      <Stack direction="row" spacing={1} mt={0.5} flexWrap="wrap" useFlexGap>
                        <Chip
                          label="التوصيل"
                          size="small"
                          color={detailsDialog.request.hasDelivery ? 'primary' : 'default'}
                          variant="outlined"
                          sx={{ opacity: detailsDialog.request.hasDelivery ? 1 : 0.45 }}
                        />
                        <Chip
                          label="الدفع الإلكتروني"
                          size="small"
                          color={detailsDialog.request.hasElectronicPayment ? 'primary' : 'default'}
                          variant="outlined"
                          sx={{ opacity: detailsDialog.request.hasElectronicPayment ? 1 : 0.45 }}
                        />
                        <Chip
                          label="التقسيط"
                          size="small"
                          color={detailsDialog.request.hasInstallment ? 'primary' : 'default'}
                          variant="outlined"
                          sx={{ opacity: detailsDialog.request.hasInstallment ? 1 : 0.45 }}
                        />
                        <Chip
                          label="استلام من المتجر"
                          size="small"
                          color={detailsDialog.request.hasStorePickup !== false ? 'primary' : 'default'}
                          variant="outlined"
                          sx={{ opacity: detailsDialog.request.hasStorePickup !== false ? 1 : 0.45 }}
                        />
                      </Stack>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">بيانات المالك</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">اسم المالك</Typography>
                      <Typography variant="body1" fontWeight="medium">{detailsDialog.request.ownerName || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">هاتف المالك</Typography>
                      <Typography variant="body1" fontWeight="medium">{detailsDialog.request.ownerPhone || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" color="text.secondary">البريد الإلكتروني</Typography>
                        <Chip label="اختياري" size="small" variant="outlined" />
                      </Stack>
                      <Typography variant="body1" fontWeight="medium">{detailsDialog.request.ownerEmail || '-'}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">المستندات</Typography>
                  <Grid container spacing={2}>
                    {detailsDialog.request.storeImageUrl && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">صورة المتجر</Typography>
                          <Chip label="اختياري" size="small" variant="outlined" />
                        </Stack>
                        <Box mt={0.5}>
                          <img
                            src={detailsDialog.request.storeImageUrl}
                            alt="صورة المتجر"
                            style={{ 
                              maxWidth: '200px', 
                              maxHeight: '150px', 
                              width: 'auto',
                              height: 'auto',
                              cursor: 'pointer',
                              borderRadius: '8px',
                              border: '1px solid #ddd',
                              objectFit: 'cover',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                            onClick={() => openImageModal(detailsDialog.request!.storeImageUrl!)}
                          />
                        </Box>
                      </Grid>
                    )}
                    {detailsDialog.request.nationalIdFrontUrl && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">بطاقة الرقم القومي (وجه)</Typography>
                          <Chip label="اختياري" size="small" variant="outlined" />
                        </Stack>
                        <Box mt={0.5}>
                          <img
                            src={detailsDialog.request.nationalIdFrontUrl}
                            alt="بطاقة الرقم القومي - وجه"
                            style={{ 
                              maxWidth: '200px', 
                              maxHeight: '150px', 
                              width: 'auto',
                              height: 'auto',
                              cursor: 'pointer',
                              borderRadius: '8px',
                              border: '1px solid #ddd',
                              objectFit: 'cover',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                            onClick={() => openImageModal(detailsDialog.request!.nationalIdFrontUrl!)}
                          />
                        </Box>
                      </Grid>
                    )}
                    {detailsDialog.request.nationalIdBackUrl && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">بطاقة الرقم القومي (ظهر)</Typography>
                          <Chip label="اختياري" size="small" variant="outlined" />
                        </Stack>
                        <Box mt={0.5}>
                          <img
                            src={detailsDialog.request.nationalIdBackUrl}
                            alt="بطاقة الرقم القومي - ظهر"
                            style={{ 
                              maxWidth: '200px', 
                              maxHeight: '150px', 
                              width: 'auto',
                              height: 'auto',
                              cursor: 'pointer',
                              borderRadius: '8px',
                              border: '1px solid #ddd',
                              objectFit: 'cover',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                            onClick={() => openImageModal(detailsDialog.request!.nationalIdBackUrl!)}
                          />
                        </Box>
                      </Grid>
                    )}
                    {detailsDialog.request.commercialRegisterUrl && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>السجل التجاري</Typography>
                        <Box mt={0.5}>
                          <img
                            src={detailsDialog.request.commercialRegisterUrl}
                            alt="السجل التجاري"
                            style={{ 
                              maxWidth: '200px', 
                              maxHeight: '150px', 
                              width: 'auto',
                              height: 'auto',
                              cursor: 'pointer',
                              borderRadius: '8px',
                              border: '1px solid #ddd',
                              objectFit: 'cover',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                            onClick={() => openImageModal(detailsDialog.request!.commercialRegisterUrl!)}
                          />
                        </Box>
                      </Grid>
                    )}
                    {detailsDialog.request.taxCardUrl && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>البطاقة الضريبية</Typography>
                        <Box mt={0.5}>
                          <img
                            src={detailsDialog.request.taxCardUrl}
                            alt="البطاقة الضريبية"
                            style={{ 
                              maxWidth: '200px', 
                              maxHeight: '150px', 
                              width: 'auto',
                              height: 'auto',
                              cursor: 'pointer',
                              borderRadius: '8px',
                              border: '1px solid #ddd',
                              objectFit: 'cover',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                            onClick={() => openImageModal(detailsDialog.request!.taxCardUrl!)}
                          />
                        </Box>
                      </Grid>
                    )}
                    {detailsDialog.request.businessLicenseUrl && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>رخصة مزاولة المهنة</Typography>
                        <Box mt={0.5}>
                          <img
                            src={detailsDialog.request.businessLicenseUrl}
                            alt="رخصة مزاولة المهنة"
                            style={{ 
                              maxWidth: '200px', 
                              maxHeight: '150px', 
                              width: 'auto',
                              height: 'auto',
                              cursor: 'pointer',
                              borderRadius: '8px',
                              border: '1px solid #ddd',
                              objectFit: 'cover',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                            onClick={() => openImageModal(detailsDialog.request!.businessLicenseUrl!)}
                          />
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>

              {detailsDialog.request.rejectionReason && (
                <Card variant="outlined" sx={{ bgcolor: 'rgba(211, 47, 47, 0.1)' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="error">سبب الرفض</Typography>
                    <Typography variant="body1">{detailsDialog.request.rejectionReason}</Typography>
                  </CardContent>
                </Card>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {detailsDialog.request && detailsDialog.request.status === 'pending' && (
            <>
              <Button 
                onClick={() => {
                  setDetailsDialog({ open: false });
                  setApproveDialog({ open: true, requestId: detailsDialog.request!.id });
                }}
                color="success"
                variant="contained"
              >
                موافقة
              </Button>
              <Button 
                onClick={() => {
                  setDetailsDialog({ open: false });
                  setRejectDialog({ open: true, requestId: detailsDialog.request!.id, reason: '' });
                }}
                color="error"
                variant="outlined"
              >
                رفض
              </Button>
            </>
          )}
          {detailsDialog.request && detailsDialog.request.status === 'approved' && (
            <Button 
              onClick={() => {
                setDetailsDialog({ open: false });
                setRejectDialog({ open: true, requestId: detailsDialog.request!.id, reason: '' });
              }}
              color="error"
              variant="outlined"
            >
              رفض الطلب (إلغاء الموافقة)
            </Button>
          )}
          <Button 
            onClick={() => {
              setDetailsDialog({ open: false });
              setDeleteDialog({ open: true, requestId: detailsDialog.request!.id });
            }}
            color="error"
            variant="outlined"
            startIcon={<DeleteIcon />}
          >
            حذف الطلب
          </Button>
          <Button onClick={() => setDetailsDialog({ open: false })}>إغلاق</Button>
        </DialogActions>
      </Dialog>

      {/* حوار الموافقة */}
      <Dialog open={approveDialog.open} onClose={() => setApproveDialog({ open: false })}>
        <DialogTitle>الموافقة على طلب تفعيل المتجر</DialogTitle>
        <DialogContent>
          <DialogContentText>
            هل أنت متأكد من الموافقة على طلب تفعيل المتجر هذا؟
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialog({ open: false })} disabled={!!actionInProgressRequestId}>إلغاء</Button>
          <Button onClick={handleApprove} color="success" variant="contained" disabled={!!actionInProgressRequestId}>
            {actionInProgressRequestId ? 'جاري التنفيذ...' : 'موافق'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* حوار الرفض */}
      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, reason: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>رفض طلب تفعيل المتجر</DialogTitle>
        <DialogContent>
          <DialogContentText mb={2}>
            يرجى إدخال سبب رفض طلب تفعيل المتجر. سيتم إرسال هذا السبب للتاجر.
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="سبب الرفض"
            value={rejectDialog.reason}
            onChange={(e) => setRejectDialog({ ...rejectDialog, reason: e.target.value })}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, reason: '' })} disabled={!!actionInProgressRequestId}>إلغاء</Button>
          <Button onClick={handleReject} color="error" variant="contained" disabled={!!actionInProgressRequestId}>
            {actionInProgressRequestId ? 'جاري التنفيذ...' : 'رفض'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* حوار الحذف */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false })}>
        <DialogTitle>حذف طلب تفعيل المتجر</DialogTitle>
        <DialogContent>
          <DialogContentText>
            هل أنت متأكد من حذف طلب تفعيل المتجر هذا؟ سيتم حذف الطلب وتحويل حالة المتجر إلى مرفوض.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false })}>إلغاء</Button>
          <Button onClick={handleDelete} color="error" variant="contained" startIcon={<DeleteIcon />}>
            حذف
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal لعرض الصور بحجم كبير */}
      <Modal
        open={imageModal.open}
        onClose={() => setImageModal({ open: false })}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={imageModal.open}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 2,
              maxWidth: '90%',
              maxHeight: '90%',
            }}
          >
            {imageModal.url && (
              <img
                src={imageModal.url}
                alt="صورة"
                style={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain' }}
              />
            )}
          </Box>
        </Fade>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  )
}

